import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportsSection from '../components/ReportsSection';
import VisaApplicationFilters from '../components/VisaApplicationFilters';
import VisaApplicationsPoolSection from '../components/VisaApplicationsPoolSection';
import VisaApplicationHistoryModal from '../components/VisaApplicationHistoryModal';
import VisaApplicationDetailModal from '../components/VisaApplicationDetailModal';
import ApplicantNameCell from '../components/ApplicantNameCell';
import VisaApplicationTableActions from '../components/VisaApplicationTableActions';
import ActionsMenuButton from '../components/ActionsMenuButton';
import CountryMultiSelect from '../components/CountryMultiSelect';
import CompanyMultiSelect from '../components/CompanyMultiSelect';
import {
  DEFAULT_VISA_FILTERS,
  ERROR_POOL_FILTERS,
  FINALIZE_POOL_FILTERS,
  filterVisaApplications,
  getApplicantName,
} from '../utils/visaApplicationFilters';
import { parseCountryIds, serializeCountryIds } from '../utils/countryUtils';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';
import { getVisaHistoryUrl } from '../utils/visaApplicationHistoryApi';
import { formatCompanyAmount, getCurrencyLabel } from '../utils/currencyUtils';
import CurrencyMinimumBalanceSection from '../components/CurrencyMinimumBalanceSection';
import { useCountries } from '../hooks/useCountries';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PATH_TO_TAB = {
  '/super-admin/dashboard': 'dashboard',
  '/super-admin/assign-me': 'assign_me',
  '/super-admin/visa-applications': 'visa_applications',
  '/super-admin/error-pool': 'error_pool',
  '/super-admin/finalize-pool': 'finalize_pool',
  '/super-admin/reassign': 'reassign',
  '/super-admin/companies': 'companies',
  '/super-admin/managers': 'managers',
  '/super-admin/currency-minimum-balances': 'currency_minimum_balances',
  '/super-admin/reports': 'reports',
};

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const getTabFromPath = () => PATH_TO_TAB[location.pathname] || 'dashboard';
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showManagerForm, setShowManagerForm] = useState(false);
  const [newManager, setNewManager] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    from_country: [],
    to_country: [],
    companies: [],
  });
  const { countries, loading: fetchingCountries } = useCountries();
  const [editManager, setEditManager] = useState(null);
  const [visaApplications, setVisaApplications] = useState([]);
  const [visaFilters, setVisaFilters] = useState(DEFAULT_VISA_FILTERS);
  const [errorPoolFilters, setErrorPoolFilters] = useState(ERROR_POOL_FILTERS);
  const [finalizePoolFilters, setFinalizePoolFilters] = useState(FINALIZE_POOL_FILTERS);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [reassignApplications, setReassignApplications] = useState([]);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [selectedNewManagerId, setSelectedNewManagerId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [eligibleManagersLoading, setEligibleManagersLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [historyApplication, setHistoryApplication] = useState(null);
  const [selectedCreditCompanyId, setSelectedCreditCompanyId] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [companyActionMenu, setCompanyActionMenu] = useState(null);
  const [managerActionMenu, setManagerActionMenu] = useState(null);

  const filteredVisaApplications = useMemo(
    () => filterVisaApplications(visaApplications, visaFilters),
    [visaApplications, visaFilters]
  );

  useEffect(() => {
    const init = async () => {
      const tab = getTabFromPath();
      setActiveTab(tab);
      await fetchDashboardData(false);
      if (tab === 'companies') await fetchCompanies();
      if (tab === 'managers') {
        await fetchManagers();
        await fetchCompanies();
      }
      if (tab === 'assign_me') await fetchAvailableApplications(false);
      if (tab === 'visa_applications') await fetchVisaApplications(false);
      if (tab === 'error_pool' || tab === 'finalize_pool') await fetchVisaApplications(false);
      if (tab === 'reassign') await fetchReassignApplications(false);
      if (tab === 'reports') await fetchReports();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const tab = getTabFromPath();
    if (tab !== activeTab) {
      setActiveTab(tab);
      handleTabData(tab);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!companyActionMenu) return;

    const closeMenu = () => setCompanyActionMenu(null);
    document.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [companyActionMenu]);

  useEffect(() => {
    if (!managerActionMenu) return;

    const closeMenu = () => setManagerActionMenu(null);
    document.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [managerActionMenu]);

  const handleTabData = (tabId) => {
    if (tabId === 'companies') fetchCompanies();
    if (tabId === 'managers') {
      fetchManagers();
      fetchCompanies();
    }
    if (tabId === 'assign_me') fetchAvailableApplications();
    if (tabId === 'visa_applications') fetchVisaApplications();
    if (tabId === 'error_pool' || tabId === 'finalize_pool') fetchVisaApplications();
    if (tabId === 'reassign') fetchReassignApplications();
    if (tabId === 'reports') fetchReports();
    if (tabId === 'dashboard') fetchDashboardData();
  };

  const fetchReports = async () => {
    setReportLoading(true);
    try {
      const res = await axios.get(`${API_URL}/super-admin/reports`);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const fetchDashboardData = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/dashboard`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/companies`);
      setCompanies(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      const status = err.response?.status;
      const message =
        err.response?.data?.error ||
        (status === 401
          ? 'Session expired. Please log in again as Super Admin.'
          : status === 403
            ? 'You do not have permission to view companies.'
            : 'Failed to load companies');
      setError(message);
    }
  };

  const fetchManagers = async () => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/managers`);
      setManagers(res.data);
    } catch (err) {
      console.error('Failed to fetch managers:', err);
      setError('Failed to load managers');
    }
  };

  const fetchAvailableApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/visa-applications/available`);
      setAvailableApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch available applications:', err);
      setError(err.response?.data?.error || 'Failed to load available visa applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchVisaApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/visa-applications`);
      setVisaApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch visa applications:', err);
      setError(err.response?.data?.error || 'Failed to load visa applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchReassignApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/reassign/applications`);
      setReassignApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch reassign applications:', err);
      setError(err.response?.data?.error || 'Failed to load assigned applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const openReassignModal = async (application) => {
    setReassignTarget(application);
    setSelectedNewManagerId('');
    setEligibleManagers([]);
    setEligibleManagersLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_URL}/super-admin/reassign/applications/${application.id}/eligible-managers`
      );
      setEligibleManagers(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load eligible managers');
      setReassignTarget(null);
    } finally {
      setEligibleManagersLoading(false);
    }
  };

  const closeReassignModal = () => {
    if (reassignLoading) return;
    setReassignTarget(null);
    setSelectedNewManagerId('');
    setEligibleManagers([]);
  };

  const confirmReassign = async () => {
    if (!reassignTarget || !selectedNewManagerId) {
      setError('Select a manager to reassign this application to');
      return;
    }

    setReassignLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/super-admin/reassign`, {
        visa_application_id: reassignTarget.id,
        new_manager_id: Number(selectedNewManagerId),
      });
      setSuccess(res.data.message);
      closeReassignModal();
      await fetchReassignApplications(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reassign application');
    } finally {
      setReassignLoading(false);
    }
  };

  const toggleCompanyStatus = async (id) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/super-admin/companies/${id}/toggle-status`);
      setSuccess('Company status updated');
      fetchCompanies();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update company status');
    }
  };

  const deleteCompany = async (company) => {
    const confirmed = window.confirm(
      `Delete ${company.name}? This will permanently remove the company and all related data.`
    );
    if (!confirmed) return;

    setError('');
    setSuccess('');
    try {
      const res = await axios.delete(`${API_URL}/super-admin/companies/${company.id}`);
      setSuccess(res.data.message);
      if (String(selectedCreditCompanyId) === String(company.id)) {
        setSelectedCreditCompanyId('');
      }
      fetchCompanies();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete company');
    }
  };

  const handleAddManager = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!newManager.name || !newManager.email || !newManager.password) {
      setError('Name, email and password are required');
      return;
    }
    if (!newManager.from_country?.length || !newManager.to_country?.length) {
      setError('From country and to country are required. Assign the manager to at least one route.');
      return;
    }
    try {
      await axios.post(`${API_URL}/super-admin/managers`, {
        ...newManager,
        from_country: serializeCountryIds(newManager.from_country),
        to_country: serializeCountryIds(newManager.to_country),
        companies: serializeCountryIds(newManager.companies),
      });
      setSuccess('Manager added successfully');
      setNewManager({
        name: '',
        email: '',
        password: '',
        phone: '',
        from_country: [],
        to_country: [],
        companies: [],
      });
      setShowManagerForm(false);
      fetchManagers();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add manager');
    }
  };

  const toggleManagerStatus = async (id) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/super-admin/managers/${id}/toggle-status`);
      setSuccess('Manager status updated');
      fetchManagers();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update manager status');
    }
  };

  const handleEditManager = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!editManager?.name || !editManager?.email) {
      setError('Name and email are required');
      return;
    }
    if (!editManager.from_country?.length || !editManager.to_country?.length) {
      setError('From country and to country are required. Assign the manager to at least one route.');
      return;
    }
    try {
      await axios.put(`${API_URL}/super-admin/managers/${editManager.id}`, {
        name: editManager.name,
        email: editManager.email,
        phone: editManager.phone,
        password: editManager.password || undefined,
        from_country: serializeCountryIds(editManager.from_country),
        to_country: serializeCountryIds(editManager.to_country),
        companies: serializeCountryIds(editManager.companies),
      });
      setSuccess('Manager updated successfully');
      setEditManager(null);
      fetchManagers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update manager');
    }
  };

  const deleteManager = async (manager) => {
    const confirmed = window.confirm(
      `Delete ${manager.name}? This will permanently remove this manager account.`
    );
    if (!confirmed) return;

    setError('');
    setSuccess('');
    try {
      const res = await axios.delete(`${API_URL}/super-admin/managers/${manager.id}`);
      setSuccess(res.data.message);
      fetchManagers();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete manager');
    }
  };

  const handleGrantCredits = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedCreditCompanyId || !creditAmount || parseFloat(creditAmount) <= 0) {
      setError('Select an company and enter a valid credit amount');
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/super-admin/companies/${selectedCreditCompanyId}/credits`,
        { amount: parseFloat(creditAmount) }
      );
      setSuccess(res.data.message);
      setCreditAmount('');
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to grant credits');
    }
  };

  const selectedCreditCompany = companies.find(
    (company) => String(company.id) === String(selectedCreditCompanyId)
  );

  const renderMessages = () => (
    <>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
          {success}
        </div>
      )}
    </>
  );

  const renderDashboard = () => (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
        <p className="text-gray-500 mt-1">Manage all companies and system users</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Total Companys</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalCompanies || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Registered companies</div>
        </div>

        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Active Companys</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.activeCompanys || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Currently active</div>
        </div>

        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Managers</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalManagers || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Internal Vughy managers</div>
        </div>

        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Visa Applications</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalVisaApplications || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Across all companies</div>
        </div>
      </div>
    </>
  );

  const toggleCompanyActionMenu = (event, company) => {
    event.stopPropagation();

    if (companyActionMenu?.company.id === company.id) {
      setCompanyActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setCompanyActionMenu({
      company,
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - 176),
    });
  };

  const renderCompanyActionMenu = () => {
    if (!companyActionMenu) return null;

    const { company, top, left } = companyActionMenu;

    return createPortal(
      <div
        className="fixed z-50 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1"
        style={{ top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            toggleCompanyStatus(company.id);
            setCompanyActionMenu(null);
          }}
          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
            company.status === 'active'
              ? 'text-red-600 hover:bg-red-50'
              : 'text-green-600 hover:bg-green-50'
          }`}
        >
          {company.status === 'active' ? 'Deactivate' : 'Activate'}
        </button>
        <button
          type="button"
          onClick={() => {
            setCompanyActionMenu(null);
            deleteCompany(company);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Delete
        </button>
      </div>,
      document.body
    );
  };

  const renderCompanies = () => (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <p className="text-gray-500 mt-1">View and manage all registered companies</p>
      </div>

      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Free Credits</h2>
        <p className="text-sm text-gray-500 mb-4">Select an company and grant free credits to their account</p>
        <form onSubmit={handleGrantCredits} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <select
              value={selectedCreditCompanyId}
              onChange={(e) => setSelectedCreditCompanyId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
              required
            >
              <option value="">Select company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name} ({company.company_registration_number})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credit Amount
              {selectedCreditCompany?.currency
                ? ` (${getCurrencyLabel(selectedCreditCompany.currency)})`
                : ''}
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
              placeholder="0.00"
              required
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={!selectedCreditCompanyId || companies.length === 0}
              className="w-full px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
            >
              Grant Free Credits
            </button>
          </div>
        </form>
        {selectedCreditCompany && (
          <p className="mt-3 text-sm text-gray-500">
            Current balance for <span className="font-medium text-gray-700">{selectedCreditCompany.name}</span>:{' '}
            <span className="font-medium text-gray-900">
              {formatCompanyAmount(selectedCreditCompany.balance, selectedCreditCompany.currency)}
            </span>
          </p>
        )}
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Registration</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Country</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Balance</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Visa Apps</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-400">No companies registered yet</td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{company.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{company.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{company.company_registration_number}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{company.country_name || company.country}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      {formatCompanyAmount(company.balance, company.currency)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{company.visa_application_count || 0}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        company.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {company.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <ActionsMenuButton
                        label={`Actions for ${company.name}`}
                        expanded={companyActionMenu?.company.id === company.id}
                        onClick={(e) => toggleCompanyActionMenu(e, company)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {renderCompanyActionMenu()}
    </>
  );

  const renderApplicationDetailModal = () => (
    <VisaApplicationDetailModal
      application={selectedApplication}
      onClose={() => setSelectedApplication(null)}
      extraFields={
        selectedApplication?.company_name
          ? [{ label: 'Company', value: selectedApplication.company_name }]
          : []
      }
    />
  );

  const renderAssignMe = () => (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Assign Me</h1>
        <p className="text-gray-500 mt-1">Unassigned visa applications across the project (view only)</p>
      </div>

      {renderApplicationDetailModal()}

      <div className="card">
        <p className="text-sm text-gray-500 mb-6">
          {availableApplications.length} unassigned application{availableApplications.length === 1 ? '' : 's'}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Company</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Visa Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Submitted</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {availableApplications.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-400">
                    No unassigned visa applications.
                  </td>
                </tr>
              ) : (
                availableApplications.map((app) => (
                  <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.company_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <ApplicantNameCell data={app.customer_visa_application_data} application={app} />
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                        {formatStatusLabel(app.application_status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4">
                      <VisaApplicationTableActions
                        app={app}
                        onView={setSelectedApplication}
                        onViewHistory={setHistoryApplication}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderVisaApplications = () => (
      <>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Visa Applications</h1>
          <p className="text-gray-500 mt-1">All visa applications across the project (view only)</p>
        </div>

        {renderApplicationDetailModal()}

        <VisaApplicationFilters
          applications={visaApplications}
          filters={visaFilters}
          onChange={setVisaFilters}
          onClear={() => setVisaFilters(DEFAULT_VISA_FILTERS)}
          showCompanyFilter
          totalCount={visaApplications.length}
          filteredCount={filteredVisaApplications.length}
        />

        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Visa Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Submitted</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-gray-400">
                      No visa applications yet.
                    </td>
                  </tr>
                ) : filteredVisaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-gray-400">No applications match your filters.</td>
                  </tr>
                ) : (
                  filteredVisaApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.company_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                      <ApplicantNameCell data={app.customer_visa_application_data} application={app} />
                    </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                          {formatStatusLabel(app.application_status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <VisaApplicationTableActions
                          app={app}
                          onView={setSelectedApplication}
                          onViewHistory={setHistoryApplication}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
  );

  const toggleManagerActionMenu = (event, manager) => {
    event.stopPropagation();

    if (managerActionMenu?.manager.id === manager.id) {
      setManagerActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setManagerActionMenu({
      manager,
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - 176),
    });
  };

  const renderManagerActionMenu = () => {
    if (!managerActionMenu) return null;

    const { manager, top, left } = managerActionMenu;

    return createPortal(
      <div
        className="fixed z-50 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1"
        style={{ top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            setEditManager({
              ...manager,
              password: '',
              from_country: parseCountryIds(manager.from_country),
              to_country: parseCountryIds(manager.to_country),
              companies: parseCountryIds(manager.companies),
            });
            setManagerActionMenu(null);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => {
            toggleManagerStatus(manager.id);
            setManagerActionMenu(null);
          }}
          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
            manager.status === 'active'
              ? 'text-red-600 hover:bg-red-50'
              : 'text-green-600 hover:bg-green-50'
          }`}
        >
          {manager.status === 'active' ? 'Deactivate' : 'Activate'}
        </button>
        <button
          type="button"
          onClick={() => {
            setManagerActionMenu(null);
            deleteManager(manager);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Delete
        </button>
      </div>,
      document.body
    );
  };

  const renderManagers = () => (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Managers</h1>
          <p className="text-gray-500 mt-1">Manage internal Vughy managers</p>
        </div>
        <button
          onClick={() => setShowManagerForm(!showManagerForm)}
          className="px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors"
        >
          {showManagerForm ? 'Cancel' : '+ Add Manager'}
        </button>
      </div>

      {editManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditManager(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Manager</h3>
            <form onSubmit={handleEditManager} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editManager.name}
                    onChange={(e) => setEditManager({ ...editManager, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editManager.email}
                    onChange={(e) => setEditManager({ ...editManager, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                  <input
                    type="text"
                    value={editManager.phone || ''}
                    onChange={(e) => setEditManager({ ...editManager, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password (optional)</label>
                  <input
                    type="password"
                    value={editManager.password || ''}
                    onChange={(e) => setEditManager({ ...editManager, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    placeholder="Leave blank to keep current"
                  />
                </div>
                <CountryMultiSelect
                  label="From Country *"
                  value={parseCountryIds(editManager.from_country)}
                  onChange={(selected) => setEditManager((prev) => ({ ...prev, from_country: selected }))}
                  countries={countries}
                  loading={fetchingCountries}
                  placeholder="Select at least one from country"
                />
                <CountryMultiSelect
                  label="To Country *"
                  value={parseCountryIds(editManager.to_country)}
                  onChange={(selected) => setEditManager((prev) => ({ ...prev, to_country: selected }))}
                  countries={countries}
                  loading={fetchingCountries}
                  placeholder="Select at least one to country"
                />
                <div className="md:col-span-2">
                  <CompanyMultiSelect
                    label="Companys"
                    value={parseCountryIds(editManager.companies)}
                    onChange={(selected) => setEditManager((prev) => ({ ...prev, companies: selected }))}
                    companies={companies}
                    loading={!companies.length}
                    placeholder="Click to select companies"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-4">
                <button
                  type="button"
                  onClick={() => setEditManager(null)}
                  className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors"
                >
                  Update Manager
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showManagerForm && (
        <form onSubmit={handleAddManager} className="mb-6 p-5 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-4">Add New Manager</h3>
          <p className="text-sm text-gray-500 mb-4">Assign at least one from/to country route for this manager.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={newManager.name}
                onChange={(e) => setNewManager({ ...newManager, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Manager name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={newManager.email}
                onChange={(e) => setNewManager({ ...newManager, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="manager@email.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <input
                type="password"
                value={newManager.password}
                onChange={(e) => setNewManager({ ...newManager, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Set password"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
              <input
                type="text"
                value={newManager.phone}
                onChange={(e) => setNewManager({ ...newManager, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Phone number"
              />
            </div>
            <CountryMultiSelect
              label="From Country *"
              value={newManager.from_country}
              onChange={(selected) => setNewManager((prev) => ({ ...prev, from_country: selected }))}
              countries={countries}
              loading={fetchingCountries}
              placeholder="Select at least one from country"
            />
            <CountryMultiSelect
              label="To Country *"
              value={newManager.to_country}
              onChange={(selected) => setNewManager((prev) => ({ ...prev, to_country: selected }))}
              countries={countries}
              loading={fetchingCountries}
              placeholder="Select at least one to country"
            />
            <div className="md:col-span-2">
              <CompanyMultiSelect
                label="Companys"
                value={newManager.companies}
                onChange={(selected) => setNewManager((prev) => ({ ...prev, companies: selected }))}
                companies={companies}
                loading={!companies.length}
                placeholder="Click to select companies"
              />
            </div>
          </div>
          <button type="submit" className="mt-4 px-6 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors">
            Add Manager
          </button>
        </form>
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Phone</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Companys</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Joined</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {managers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-400">No managers yet</td>
                </tr>
              ) : (
                managers.map((manager) => (
                  <tr key={manager.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{manager.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.phone || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.from_country_name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.to_country_name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.company_names || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        manager.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {manager.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {new Date(manager.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <ActionsMenuButton
                        label={`Actions for ${manager.name}`}
                        expanded={managerActionMenu?.manager.id === manager.id}
                        onClick={(e) => toggleManagerActionMenu(e, manager)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {renderManagerActionMenu()}
    </>
  );

  const renderReassign = () => {
    const reassignModal = reassignTarget ? (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={closeReassignModal}
      >
        <div
          className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Reassign Application</h3>
          <p className="text-sm text-gray-600 mb-4">
            {reassignTarget.company_name} — {reassignTarget.passenger_id}
          </p>
          <div className="space-y-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Current manager:</span>{' '}
              {reassignTarget.manager_name}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New manager</label>
              {eligibleManagersLoading ? (
                <p className="text-gray-400 text-sm">Loading eligible managers...</p>
              ) : eligibleManagers.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No other active managers match this application&apos;s route and companies.
                </p>
              ) : (
                <select
                  value={selectedNewManagerId}
                  onChange={(e) => setSelectedNewManagerId(e.target.value)}
                  disabled={reassignLoading}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                >
                  <option value="">Select a manager</option>
                  {eligibleManagers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.name} ({manager.email})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={closeReassignModal}
              disabled={reassignLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmReassign}
              disabled={reassignLoading || !selectedNewManagerId || eligibleManagers.length === 0}
              className="px-4 py-2 text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors disabled:opacity-50"
            >
              {reassignLoading ? 'Reassigning...' : 'Confirm Reassign'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

    return (
      <>
        {reassignModal}

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Reassign</h1>
          <p className="text-gray-500 mt-1">
            Move assigned visa applications from one manager to another
          </p>
        </div>

        <div className="card">
          <p className="text-sm text-gray-500 mb-6">
            {reassignApplications.length} assigned application{reassignApplications.length === 1 ? '' : 's'}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Current Manager</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Assigned</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reassignApplications.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">
                      No manager-assigned applications to reassign.
                    </td>
                  </tr>
                ) : (
                  reassignApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.company_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        <ApplicantNameCell data={app.customer_visa_application_data} application={app} />
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.manager_name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                          {formatStatusLabel(app.application_status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(app.assigned_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => openReassignModal(app)}
                          className="text-sm px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Reassign
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderReports = () => (
    <ReportsSection
      title="System Reports"
      subtitle="Complete overview of visa applications, companies, and system activity"
      report={report}
      loading={reportLoading}
    />
  );

  const renderErrorPool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Error Pool"
        subtitle="All visa applications with error status across the project"
        applications={visaApplications}
        filters={errorPoolFilters}
        onFiltersChange={setErrorPoolFilters}
        onClearFilters={() => setErrorPoolFilters(ERROR_POOL_FILTERS)}
        showCompanyFilter
        showCompanyColumn
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage="No error applications in the pool."
      />
    </>
  );

  const renderFinalizePool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Finalize Pool"
        subtitle="All finalized visa applications across the project"
        applications={visaApplications}
        filters={finalizePoolFilters}
        onFiltersChange={setFinalizePoolFilters}
        onClearFilters={() => setFinalizePoolFilters(FINALIZE_POOL_FILTERS)}
        showCompanyFilter
        showCompanyColumn
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage="No finalized applications in the pool."
      />
    </>
  );

  if (loading && activeTab !== 'reports' && activeTab !== 'visa_applications' && activeTab !== 'error_pool' && activeTab !== 'finalize_pool' && activeTab !== 'assign_me' && activeTab !== 'reassign' && activeTab !== 'currency_minimum_balances') {
    return (
      <Layout>
        <LoadingSpinner text="Loading dashboard..." />
      </Layout>
    );
  }

  return (
    <Layout>
      {renderMessages()}

      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'assign_me' && renderAssignMe()}
      {activeTab === 'visa_applications' && renderVisaApplications()}
      {activeTab === 'error_pool' && renderErrorPool()}
      {activeTab === 'finalize_pool' && renderFinalizePool()}
      {activeTab === 'reassign' && renderReassign()}
      {activeTab === 'companies' && renderCompanies()}
      {activeTab === 'managers' && renderManagers()}
      {activeTab === 'currency_minimum_balances' && <CurrencyMinimumBalanceSection />}
      {activeTab === 'reports' && renderReports()}

      {historyApplication && (
        <VisaApplicationHistoryModal
          application={historyApplication}
          historyUrl={getVisaHistoryUrl(historyApplication.id, user?.role)}
          onClose={() => setHistoryApplication(null)}
        />
      )}
    </Layout>
  );
};

export default SuperAdminDashboard;
